/// Pure helpers for the live-traffic *shift* framing (position select +
/// aircraft-worked goal). Kept free of GameSession/Aircraft imports so the
/// AI-gating + completion decisions are unit-testable in isolation.
///
/// Ports the iOS reference (HandoffSetupView / LiveRegion / GameViewModel):
/// the player picks a position (a set of airports to work); arrivals to
/// airports they aren't working fly themselves (AI); the shift ends when the
/// player has worked the target number of aircraft.

import type { LiveShiftPosition } from '../types/LevelData'

/// Structural slice of an AircraftGoal — just the fields that name an airport
/// the player would *land* (vs a pass-through/transition, which names a fix).
export interface ShiftGoalLike {
  land?: { airportName: string }
  arrival?: { airportName: string }
  landHelipad?: { airportName: string }
}

/// The airports the player works at the chosen position. Unknown id ⟹ empty
/// (caller treats empty as "no positioned shift" upstream).
export function resolveShiftAirports(
  positions: LiveShiftPosition[] | undefined,
  positionId: string,
): Set<string> {
  const p = positions?.find((x) => x.id === positionId)
  return new Set(p ? p.airports : [])
}

/// The airport an aircraft is arriving to (its land target), or null if it's a
/// pass-through (transition/departure) with no owned airport.
export function workedAirportOf(goal: ShiftGoalLike): string | null {
  return (
    goal.land?.airportName ??
    goal.arrival?.airportName ??
    goal.landHelipad?.airportName ??
    null
  )
}

/// In a positioned shift, an arrival to an airport the player isn't working
/// flies itself (forced AI). Pass-through traffic (no owned airport) and the
/// no-shift case (`selected == null`) are never forced — they fall through to
/// the normal aiControlledPercentage roll.
export function shouldForceAI(
  workedAirport: string | null,
  selected: Set<string> | null,
): boolean {
  if (!selected || workedAirport == null) return false
  return !selected.has(workedAirport)
}

/// The shift is complete once the player has worked at least `shiftGoal`
/// aircraft. A goal of 0 means "no goal" (open-ended) and never completes here.
export function isShiftComplete(aircraftWorked: number, shiftGoal: number): boolean {
  return shiftGoal > 0 && aircraftWorked >= shiftGoal
}
